import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// The JSON list you requested
const COLOR_PALETTE = [
  { "group": "Essentials", "name": "Ink Black", "hex": "#0F172A" },
  { "group": "Essentials", "name": "Graphite", "hex": "#374151" },
  { "group": "Essentials", "name": "Correction Red", "hex": "#DC2626" },
  { "group": "Essentials", "name": "Action Blue", "hex": "#1D4ED8" },
  { "group": "Essentials", "name": "Approval Green", "hex": "#15803D" },

  { "group": "Redlines", "name": "Revision Red", "hex": "#EF4444" },
  { "group": "Redlines", "name": "Demo Magenta", "hex": "#C026D3" },
  { "group": "Redlines", "name": "New Work Cyan", "hex": "#0891B2" },
  { "group": "Redlines", "name": "Alert Orange", "hex": "#EA580C" },
  { "group": "Redlines", "name": "Comment Purple", "hex": "#7C3AED" },

  { "group": "Zoning", "name": "Residential Yellow", "hex": "#FEF08A" },
  { "group": "Zoning", "name": "Commercial Red", "hex": "#FECACA" },
  { "group": "Zoning", "name": "Open Space Green", "hex": "#BBF7D0" },
  { "group": "Zoning", "name": "Industrial Grey", "hex": "#E5E7EB" },
  { "group": "Zoning", "name": "Institutional Blue", "hex": "#BFDBFE" },

  { "group": "Materials", "name": "Brick Red", "hex": "#9C4221" },
  { "group": "Materials", "name": "Concrete Grey", "hex": "#9CA3AF" },
  { "group": "Materials", "name": "Timber Brown", "hex": "#78350F" },
  { "group": "Materials", "name": "Earth Gold", "hex": "#D97706" },
  { "group": "Materials", "name": "Vegetation", "hex": "#4D7C0F" },

  { "group": "Highlighters", "name": "Neon Yellow", "hex": "#FFFF00" },
  { "group": "Highlighters", "name": "Neon Green", "hex": "#00FF00" },
  { "group": "Highlighters", "name": "Neon Pink", "hex": "#FF00FF" },
  { "group": "Highlighters", "name": "Neon Cyan", "hex": "#00FFFF" },
  { "group": "Highlighters", "name": "Neon Orange", "hex": "#FFA500" },

  { "group": "MEP Trades", "name": "Supply Air", "hex": "#2563EB" },
  { "group": "MEP Trades", "name": "Return Air", "hex": "#DB2777" },
  { "group": "MEP Trades", "name": "Sanitary", "hex": "#047857" },
  { "group": "MEP Trades", "name": "Gas Line", "hex": "#F59E0B" },
  { "group": "MEP Trades", "name": "Electrical", "hex": "#B91C1C" },

  { "group": "Modern UI", "name": "Teal", "hex": "#14B8A6" },
  { "group": "Modern UI", "name": "Indigo", "hex": "#4F46E5" },
  { "group": "Modern UI", "name": "Rose", "hex": "#F43F5E" },
  { "group": "Modern UI", "name": "Lime", "hex": "#84CC16" },
  { "group": "Modern UI", "name": "Violet", "hex": "#8B5CF6" },

  //   { "group": "Cool Greys", "name": "Cool Grey 100", "hex": "#F3F4F6" },
  //   { "group": "Cool Greys", "name": "Cool Grey 300", "hex": "#D1D5DB" },
  //   { "group": "Cool Greys", "name": "Cool Grey 500", "hex": "#6B7280" },
  //   { "group": "Cool Greys", "name": "Cool Grey 700", "hex": "#374151" },
  //   { "group": "Cool Greys", "name": "Cool Grey 900", "hex": "#111827" },

  //   { "group": "Warm Greys", "name": "Warm Grey 100", "hex": "#F5F5F4" },
  //   { "group": "Warm Greys", "name": "Warm Grey 300", "hex": "#D6D3D1" },
  //   { "group": "Warm Greys", "name": "Warm Grey 500", "hex": "#78716C" },
  //   { "group": "Warm Greys", "name": "Warm Grey 700", "hex": "#44403C" },
  //   { "group": "Warm Greys", "name": "Warm Grey 900", "hex": "#1C1917" },

  { "group": "Status", "name": "Pending", "hex": "#94A3B8" },
  { "group": "Status", "name": "In Progress", "hex": "#3B82F6" },
  { "group": "Status", "name": "Review", "hex": "#F59E0B" },
  { "group": "Status", "name": "Complete", "hex": "#10B981" },
  { "group": "Status", "name": "Rejected", "hex": "#EF4444" }
]

const ColorPicker = ({ value, onChange, title, autoOpen = false, size = 'default', 'data-tt': dataTt }) => {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [dropdownStyle, setDropdownStyle] = useState({ opacity: 0, pointerEvents: 'none' });
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  // Position dropdown within viewport on open
  useEffect(() => {
    if (!isOpen) {
      setDropdownStyle({ opacity: 0, pointerEvents: 'none' });
      return;
    }
    const trigger = wrapperRef.current?.querySelector('.color-picker-trigger');
    const content = contentRef.current;
    if (!trigger || !content) return;

    const triggerRect = trigger.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const gap = 4;
    const margin = 8;

    let top = triggerRect.bottom + gap;
    let left = triggerRect.left;

    const overflowRight = (left + contentRect.width + margin) - window.innerWidth;
    if (overflowRight > 0) {
      left = Math.max(margin, left - overflowRight);
    }

    const overflowBottom = (top + contentRect.height + margin) - window.innerHeight;
    if (overflowBottom > 0) {
      top = Math.max(margin, triggerRect.top - contentRect.height - gap);
    }

    if (left < margin) left = margin;
    if (top < margin) top = margin;

    setDropdownStyle({
      top: `${top}px`,
      left: `${left}px`,
      bottom: 'auto',
      right: 'auto',
      opacity: 1,
      pointerEvents: 'auto',
    });
  }, [isOpen]);

  // Group colors for display
  const groupedColors = COLOR_PALETTE.reduce((acc, color) => {
    if (!acc[color.group]) acc[color.group] = [];
    acc[color.group].push(color);
    return acc;
  }, {});

  // Close dropdown if clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsideWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const clickedInsideContent = contentRef.current && contentRef.current.contains(event.target);
      if (!clickedInsideWrapper && !clickedInsideContent) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`color-picker-wrapper ${size === 'small' ? 'size-small' : ''}`} ref={wrapperRef} title={title}>
      {/* Trigger Button (Shows current color) */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="color-picker-trigger"
        style={{ backgroundColor: value }}
        data-tt={dataTt}
        aria-label={title}
      />

      {/* Dropdown Menu */}
      {isOpen && createPortal(
        <div ref={contentRef} className="color-picker-content" style={dropdownStyle}>
          {Object.entries(groupedColors).map(([groupName, colors]) => (
            <div key={groupName} className="color-picker-group">
              <h4 className="color-picker-group-header">
                {groupName}
              </h4>
              <div className="color-picker-color-grid">
                {colors.map((color) => (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => {
                      onChange(color.hex);
                      setIsOpen(false);
                    }}
                    title={color.name}
                    className={`color-picker-color-button ${value === color.hex ? 'selected' : ''}`}
                    style={{ backgroundColor: color.hex }}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Fallback to custom native picker if they want specific color */}
          <div className="color-picker-custom">
            <span className="color-picker-custom-label">Custom:</span>
            <input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="color-picker-custom-input"
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColorPicker;
